import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { instancesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useNivelAcesso } from '../hooks/useNivelAcesso';

const ZApiConfig = ({ instance, onConfigSaved }) => {
  const { darkMode } = useTheme();
  const { isAdmin } = useNivelAcesso();
  const [instanceId, setInstanceId] = useState(instance?.z_api_instance_id || '');
  const [token, setToken] = useState(instance?.z_api_token || '');
  const [saving, setSaving] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (instance?.provider === 'z-api' && instance?.z_api_instance_id && instance?.z_api_token) {
      loadQrCode();
      const interval = setInterval(loadQrCode, 15000);
      return () => clearInterval(interval);
    }
  }, [instance?.provider, instance?.z_api_instance_id, instance?.z_api_token]);

  const loadQrCode = async () => {
    if (!instance?.id) return;
    
    setLoadingQr(true);
    try {
      // Primeiro verifica o status
      const statusResponse = await instancesAPI.verifyConnection(instance.id);
      
      if (statusResponse.data?.connected) {
        setQrCode(null);
        toast.success('WhatsApp conectado com sucesso!');
        onConfigSaved();
        return;
      }
      
      // Se não conectado, busca QR Code
      const response = await instancesAPI.getZApiQrCode(instance.id);
      if (response.data?.value) {
        setQrCode(response.data.value);
      }
    } catch (error) {
      console.error('Erro ao carregar QR Code:', error);
      if (error.response?.status !== 400) {
        toast.error('Erro ao carregar QR Code');
      }
    } finally {
      setLoadingQr(false);
    }
  };

  const handleSave = async () => {
    if (!instanceId || !token) {
      toast.error('Preencha Instance ID e Token');
      return;
    }

    setSaving(true);
    try {
      const response = await instancesAPI.configurarZApi(instance.id, {
        z_api_instance_id: instanceId,
        z_api_token: token,
      });

      toast.success('Z-API configurada com sucesso!');
      
      // Atualizar a instância localmente para disparar o useEffect
      if (response.data?.data) {
        Object.assign(instance, response.data.data);
      }
      
      onConfigSaved();
      
      // Aguardar um pouco e carregar o QR Code
      setTimeout(() => loadQrCode(), 1000);
    } catch (error) {
      toast.error('Erro ao configurar Z-API');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = instance?.provider === 'z-api' && instance?.z_api_instance_id && instance?.z_api_token;
  const isConnected = instance?.status === 'conectada';

  const handleDisconnect = async () => {
    if (!instance?.id) return;

    setDisconnecting(true);
    try {
      await instancesAPI.disconnectZApi(instance.id);
      toast.success('WhatsApp desconectado com sucesso!');
      onConfigSaved();
    } catch (error) {
      toast.error('Erro ao desconectar WhatsApp');
      console.error('Erro ao desconectar:', error);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      {isConfigured && !isConnected && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-50'} border border-green-500`}>
          <div className="flex flex-col items-center space-y-3">
            <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              📱 Escaneie o QR Code com seu WhatsApp
            </h4>
            {qrCode ? (
              <>
                <div className="bg-white p-4 rounded-lg">
                  <img src={qrCode} alt="QR Code Z-API" className="w-64 h-64" />
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {loadingQr ? 'Atualizando QR Code...' : 'QR Code atualiza a cada 15 segundos'}
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {loadingQr ? 'Carregando QR Code...' : 'Clique no botão para gerar o QR Code'}
                </p>
                <button
                  onClick={loadQrCode}
                  disabled={loadingQr}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loadingQr ? 'Carregando...' : 'Gerar QR Code'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isConfigured && isConnected && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-50'} border border-green-500`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-3xl">✅</div>
              <div>
                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  WhatsApp Conectado
                </h4>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Sua instância Z-API está conectada e funcionando
                </p>
              </div>
            </div>
            {isAdmin() && (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {disconnecting ? 'Desconectando...' : 'Desconectar'}
              </button>
            )}
          </div>
        </div>
      )}

      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Instance ID *
        </label>
        <input
          type="text"
          value={instanceId}
          onChange={(e) => setInstanceId(e.target.value)}
          placeholder="Ex: 3C4B5A6D7E8F"
          className={`w-full px-3 py-2 rounded-lg border ${
            darkMode
              ? 'bg-zinc-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Token *
        </label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Token de autenticação"
          className={`w-full px-3 py-2 rounded-lg border ${
            darkMode
              ? 'bg-zinc-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      </div>

      <div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
      >
        {saving ? 'Salvando...' : 'Salvar Configuração'}
      </button>
    </div>
  );
};

export default ZApiConfig;
